/**
 * app-state — 把 AgentEvent 流折叠成可渲染的 chat 视图模型（纯函数，node 可测）
 *
 * 把状态逻辑从组件里抽出来（brainstorming 的隔离原则）：组件只渲染 AppState，
 * 交互测试因此不需要 DOM/jsdom。不可变更新。
 */

import type { AgentEvent } from "./api/client.js";
import type { AgentConfirmationBrief } from "./views/agent/AgentConfirmation.js";

export interface ChatTurn {
  readonly role: "user" | "agent";
  readonly text: string;
}

export interface ToolActivity {
  readonly id: string;
  readonly name: string;
  readonly args?: Record<string, unknown>;
  readonly ok?: boolean;
  readonly detail?: string;
}

export type GenStatus = "idle" | "running" | "waiting_confirmation" | "done" | "error";

/** The agent's end-of-turn quality self-score, surfaced for the user to act on. */
export interface QualityScore {
  readonly score: number;
  readonly strengths?: string;
  readonly improvements?: readonly string[];
}

export interface AppState {
  readonly turns: readonly ChatTurn[];
  readonly tools: readonly ToolActivity[];
  readonly status: GenStatus;
  readonly error?: string;
  /** The most recent run's quality self-score (from the `score` event). */
  readonly quality?: QualityScore;
  /** A confirmation checkpoint requested by the running agent. */
  readonly pendingConfirmation?: AgentConfirmationBrief;
}

export const initialAppState: AppState = { turns: [], tools: [], status: "idle" };

/** Begin a user turn: record the prompt and open a fresh (empty) agent turn. */
export function startUserTurn(state: AppState, text: string): AppState {
  return {
    turns: [...state.turns, { role: "user", text }, { role: "agent", text: "" }],
    tools: [],
    status: "running",
    error: undefined,
    quality: undefined,
    pendingConfirmation: undefined,
  };
}

function confirmationBriefFromEvent(event: Extract<AgentEvent, { type: "confirmation.request" }>): AgentConfirmationBrief {
  return {
    kind: event.request.kind,
    title: event.request.title,
    summary: event.request.summary,
    contextItems: event.request.contextItems ?? [],
    questions: event.request.questions,
    steps: event.request.steps ?? [],
  };
}

function appendToLastAgent(turns: readonly ChatTurn[], text: string): ChatTurn[] {
  const last = turns[turns.length - 1];
  if (last && last.role === "agent") {
    return [...turns.slice(0, -1), { role: "agent", text: last.text + text }];
  }
  return [...turns, { role: "agent", text }];
}

function ensureAgentText(turns: readonly ChatTurn[], fallback: string): ChatTurn[] {
  const last = turns[turns.length - 1];
  if (last && last.role === "agent" && last.text.length === 0 && fallback.length > 0) {
    return [...turns.slice(0, -1), { role: "agent", text: fallback }];
  }
  return [...turns];
}

export function reduceAgentEvent(state: AppState, event: AgentEvent): AppState {
  switch (event.type) {
    case "session.start":
      return { ...state, status: "running" };
    case "output.delta":
      return { ...state, turns: appendToLastAgent(state.turns, event.text) };
    case "tool.start": {
      const tool: ToolActivity = event.args
        ? { id: event.toolCallId, name: event.name, args: event.args }
        : { id: event.toolCallId, name: event.name };
      return { ...state, tools: [...state.tools, tool] };
    }
    case "tool.end":
      return {
        ...state,
        tools: state.tools.map((t) => (t.id === event.toolCallId ? { ...t, ok: event.ok, detail: event.detail } : t)),
      };
    case "progress":
      return state;
    case "score":
      return {
        ...state,
        quality: { score: event.score, strengths: event.strengths, improvements: event.improvements },
      };
    case "confirmation.request":
      return {
        ...state,
        status: "waiting_confirmation",
        pendingConfirmation: confirmationBriefFromEvent(event),
      };
    case "done":
      return { ...state, status: "done", turns: ensureAgentText(state.turns, event.result.output) };
    case "error":
      return { ...state, status: "error", error: event.error.message };
    default:
      return state;
  }
}
